import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Typography,
  PolicyRow,
  SelectPolicy,
  Icon,
  Tooltip,
  Input,
  MaskInput,
  DIALOG_TYPES,
  Dialog,
  Link,
  Spinner,
  FormField,
  FormFieldIcon,
} from 'components/alf-design';
import { formats, useBriteVerify, useForm } from 'utils';
import { capitalize } from 'utils/formats';
import {
  isSCIPXPolicy,
  isD3RIPolicy,
  isH3TXPolicy,
  isD1FLPolicy,
  isIPXFLDP1Policy,
  isCompany,
} from 'utils/policyType';
import goPaperlessIcon from 'assets/icon/GoPaperlessIcon.svg';
import { LegacyColors } from 'constants';
import config from 'config';
import { datadogLogs } from '@datadog/browser-logs';

const formatName = (value) => (value ? capitalize(value) : '—');

const hasProfileGoPaperlessWording = (policy) => {
  if (policy?.policySystem === 'Violet') {
    return Boolean(
      isD3RIPolicy(policy?.policyNumber) ||
      isH3TXPolicy(policy?.policyNumber) ||
      isD1FLPolicy(policy?.policyNumber),
    );
  }

  if (policy?.policySystem === 'IPX') {
    return Boolean(isIPXFLDP1Policy(policy?.policyNumber));
  }

  return false;
};

const definePaperlessWording = (policy) => {
  const isGoPaperless = hasProfileGoPaperlessWording(policy);

  if (policy?.billing?.isPaperless) {
    if (isGoPaperless) {
      return {
        title: 'Go Paperless',
        tooltipTitle: 'Go paperless information',
        description: 'This policy is enrolled in electronic delivery.',
        descriptionAfterChange: 'Receive billing and documents electronically.',
        status: 'Enrolled',
        statusAfterChange: 'Pending Unenrollment',
        buttonType: 'secondary',
        buttonText: 'Unenroll from Paperless',
        modalTitle: 'Direct Mail Document Delivery',
        modalSubtitle: 'Warning!',
        modalText:
          'Your policy and billing documents will be delivered via direct mail.',
        modalQuestion:
          'Are you sure you want to switch to direct mail delivery?',
        buttonModalText: 'Yes, Switch to Direct Mail',
        leafIcon: false,
      };
    } else {
      return {
        title: 'Paperless Discount',
        tooltipTitle: 'Paperless discount information',
        description: 'This policy is enrolled in paperless discount.',
        descriptionAfterChange:
          'Save money by receiving billing and documents electronically.',
        status: 'Enrolled',
        statusAfterChange: 'Pending Unenrollment',
        buttonType: 'secondary',
        buttonText: 'Unenroll from Paperless Discount',
        modalTitle: 'Direct Mail Document Delivery',
        modalSubtitle: 'Warning!',
        modalText:
          'Unenrolling from Paperless Discount will increase your premium. Your policy and billing documents will be delivered via direct mail.',
        modalQuestion:
          'Are you sure you want to switch to direct mail delivery?',
        buttonModalText: 'Yes, Switch to Direct Mail',
        leafIcon: false,
      };
    }
  } else {
    if (isGoPaperless) {
      return {
        title: 'Go Paperless',
        tooltipTitle: 'Go paperless information',
        description: 'Receive billing and documents electronically.',
        descriptionAfterChange:
          'This policy is enrolled in electronic delivery.',
        status: 'Unenrolled',
        statusAfterChange: 'Pending Enrollment',
        buttonType: 'primary',
        buttonText: 'Enroll in Paperless',
        modalTitle: 'Electronic Delivery',
        modalSubtitle: 'Go Paperless',
        modalText:
          'With the Paperless, you will receive electronic delivery of billing and policy documents.',
        modalQuestion:
          'Are you sure you want to switch to paperless document delivery?',
        buttonModalText: 'Yes, Go Paperless',
        leafIcon: true,
      };
    } else {
      return {
        title: 'Paperless Discount',
        tooltipTitle: 'Paperless discount information',
        description:
          'Save money by receiving billing and documents electronically.',
        descriptionAfterChange:
          'This policy is enrolled in paperless discount.',
        status: 'Unenrolled',
        statusAfterChange: 'Pending Enrollment',
        buttonType: 'primary',
        buttonText: 'Enroll in Paperless Discount',
        modalTitle: 'Paperless Discount',
        modalSubtitle: 'Go Paperless',
        modalText:
          'Enrolling in Paperless Discount can decrease your premium. You will receive electronic delivery of billing and policy documents.',
        modalQuestion:
          'Are you sure you want to switch to paperless document delivery?',
        buttonModalText: 'Yes, Go Paperless',
        leafIcon: false,
      };
    }
  }
};

function validate(values) {
  const errors = {};

  if (!values.phone || (values.phone || '').replace(/\D/g, '').length < 10) {
    errors.phone = 'Please provide a valid email address.';
  }

  if (!/.+@.+\..+/.test(values.email)) {
    errors.email = 'Please provide a valid email address.';
  }
  return errors;
}

const getInsuredInfo = (selectedPolicy, selectedPolicyFusionNH) => {
  if (
    selectedPolicy?.isFusion &&
    selectedPolicy?.status !== 'Active' &&
    selectedPolicyFusionNH?.status === 'Active'
  ) {
    return {
      firstName: selectedPolicyFusionNH?.insured?.firstName,
      lastName: selectedPolicyFusionNH?.insured?.lastName,
      email: selectedPolicyFusionNH?.insured?.email,
      phone: selectedPolicyFusionNH?.insured?.phone,
      paperlessEnrolled: selectedPolicyFusionNH.billing?.isPaperless,
    };
  }

  return {
    firstName: selectedPolicy?.insured?.firstName,
    lastName: selectedPolicy?.insured?.lastName,
    email: selectedPolicy?.insured?.email,
    phone: selectedPolicy?.insured?.phone,
    paperlessEnrolled: selectedPolicy?.billing?.isPaperless,
  };
};

const PolicyContactCard = ({
  api,
  policies,
  selectedPolicy = null,
  selectedPolicyFusionNH = null,
  loading = false,
  fetchPolicy,
}) => {
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const policyContactCardRef = useRef(null);
  const pendingSelectPolicyRef = useRef(null);
  const pendingInAppLinkHrefRef = useRef(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [paperlessChange, setPaperlessChange] = useState(false);
  const [blurred, setBlurred] = useState({
    email: false,
    phone: false,
    isPaperless: false,
  });
  const [showProfileSaveSuccessBanner, setShowProfileSaveSuccessBanner] =
    useState(false);
  const [showProfileSaveErrorBanner, setShowProfileSaveErrorBanner] =
    useState(false);
  const [openSelectPolicy, setOpenSelectPolicy] = useState(false);
  const [showUnsavedPolicyModal, setShowUnsavedPolicyModal] = useState(false);
  const [showConsentAckModal, setShowConsentAckModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const insuredInfo = getInsuredInfo(selectedPolicy, selectedPolicyFusionNH);
  const firstName = insuredInfo.firstName;
  const lastName = insuredInfo.lastName;
  const contactEmail = insuredInfo.email;
  const phone = insuredInfo.phone;
  const contactPhone = formats.phoneFormat(phone) || phone || '';
  const isPaperless = insuredInfo.paperlessEnrolled;
  const enableEndorsement = Boolean(selectedPolicy?.billing?.enableEndorsement);
  const paperless = definePaperlessWording(selectedPolicy);
  const showPaperlessSection = !isSCIPXPolicy(selectedPolicy?.policyNumber);
  const multiplePolicies = policies.length > 1;
  const organizationName = selectedPolicy?.organizationName?.trim();
  const showCompanyName = isCompany(selectedPolicy);
  const disableEndorsements =
    String(config.disableEndorsement).toLowerCase() === 'true' ||
    (selectedPolicy?.policySystem === 'IPX' &&
      String(config.disableIPXEndorsement).toLowerCase() === 'true') ||
    (selectedPolicy?.isFusion &&
      String(config.disableFusionEndorsement).toLowerCase() === 'true') ||
    (selectedPolicy?.isFusion
      ? selectedPolicy?.status !== 'Active' &&
        selectedPolicyFusionNH?.status !== 'Active'
      : selectedPolicy?.status !== 'Active');

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFormValues,
    values,
    errors,
    touched,
    isSubmitting,
    setSubmitting,
  } = useForm(
    { email: contactEmail, phone: contactPhone, isPaperless: isPaperless },
    validate,
    true,
  );

  const syncFormFromPolicyContact = () => {
    setFormValues(
      {
        email: contactEmail,
        phone: contactPhone,
        isPaperless: isPaperless,
      },
      false,
    );
  };

  useEffect(() => {
    syncFormFromPolicyContact();
  }, [contactEmail, contactPhone, isPaperless, selectedPolicy?.policyNumber]);

  const markBlurred = (field, e) => {
    setBlurred((prev) => ({ ...prev, [field]: true }));
    handleBlur(e);
  };

  const emailStatus = useBriteVerify(
    values.email,
    touched.email,
    blurred.email,
  );

  const saveDisable =
    (values.email === contactEmail &&
      values.phone === contactPhone &&
      values.isPaperless === isPaperless) ||
    isSubmitting ||
    Object.keys(errors).length > 0 ||
    (values.email !== contactEmail && emailStatus !== 'valid');

  const hasPendingProfileChanges = () => {
    if (
      values.email !== contactEmail ||
      values.phone !== contactPhone ||
      values.isPaperless !== isPaperless
    ) {
      return true;
    }
    return false;
  };

  const setPendingPolicySelect = (policy) => {
    pendingInAppLinkHrefRef.current = null;
    pendingSelectPolicyRef.current = policy;
    setShowUnsavedPolicyModal(true);
  };

  const handlePolicySelect = (policy) => {
    if (isEditMode && hasPendingProfileChanges()) {
      setPendingPolicySelect(policy);
      setOpenSelectPolicy(false);
      return;
    }
    navigate(
      `/user-profile?policy=${encodeURIComponent(policy?.policyNumber)}`,
      { replace: true },
    );
    setShowProfileSaveSuccessBanner(false);
    setShowProfileSaveErrorBanner(false);
    setOpenSelectPolicy(false);
  };

  const handleCloseEdit = () => {
    if (isEditMode && hasPendingProfileChanges()) {
      setShowUnsavedPolicyModal(true);
      return;
    }
    setIsEditMode(false);
  };

  const handleDiscardChanges = () => {
    setPaperlessChange(false);
    setFormValues({
      email: contactEmail,
      phone: contactPhone,
      isPaperless: isPaperless,
    });
    setShowUnsavedPolicyModal(false);
    setIsEditMode(false);
  };

  const handleSaveAndClose = () => {
    handleProfileSave();
    setShowUnsavedPolicyModal(false);
  };

  const handleProfileSave = (e) => {
    handleSubmit(e, () => {
      if (!selectedPolicy) {
        return;
      }

      const originalDate = new Date(selectedPolicy?.effectiveDate);
      const now = new Date();
      const todayString = now.toISOString().split('T')[0];
      const originalString = selectedPolicy?.effectiveDate?.split('T')[0];
      const effectiveDateToSend =
        originalDate > now ? originalString : todayString;

      const policyIdentifier =
        selectedPolicy?.isFusion &&
        selectedPolicy?.status !== 'Active' &&
        selectedPolicyFusionNH?.status === 'Active'
          ? selectedPolicyFusionNH?.policyNumber
          : selectedPolicy?.policyNumber;

      const body = {
        Email: values.email,
        Phone: values.phone.replace(/\D/g, ''),
        EffectiveDate: effectiveDateToSend,
        ...(showPaperlessSection && { SetPaperless: values.isPaperless }),
      };

      if (!hasPendingProfileChanges()) {
        return;
      }

      setShowProfileSaveErrorBanner(false);

      api.withAuth
        .postPolicyEndorsements(policyIdentifier, body)
        .then(() => {
          const pendingPolicyAfter = pendingSelectPolicyRef.current;
          const pendingHrefAfter = pendingInAppLinkHrefRef.current;
          if (pendingPolicyAfter) {
            pendingSelectPolicyRef.current = null;
          }
          if (pendingHrefAfter) {
            pendingInAppLinkHrefRef.current = null;
          }
          setPaperlessChange(false);
          setIsEditMode(false);
          setShowProfileSaveErrorBanner(false);
          setShowProfileSaveSuccessBanner(true);
          if (pendingPolicyAfter) {
            navigate(
              `/user-profile?policy=${encodeURIComponent(
                pendingPolicyAfter.policyNumber,
              )}`,
              { replace: true },
            );
          } else if (pendingHrefAfter) {
            navigate(pendingHrefAfter, { replace: true });
          }
          const refetchForPolicy = pendingPolicyAfter
            ? pendingPolicyAfter.policyNumber
            : selectedPolicy?.policyNumber;
          return fetchPolicy(
            refetchForPolicy,
            null,
            (err) => {
              if (err) {
                datadogLogs.logger.error(`Get Policy - ${refetchForPolicy}`, {
                  err,
                });
              }
            },
            { force: true },
          );
        })
        .catch((err) => {
          datadogLogs.logger.error(
            `Post endorsements - ${selectedPolicy?.policyNumber}`,
            { err },
          );
          setShowProfileSaveSuccessBanner(false);
          setShowProfileSaveErrorBanner(true);
          setSubmitting(false);
          handleDiscardChanges();
        })
        .finally(() => {
          setSubmitting(false);
          handleDiscardChanges();
        });
    });
  };

  const handleUnsavedPolicyDiscard = () => {
    setShowUnsavedPolicyModal(false);
    const pending = pendingSelectPolicyRef.current;
    const href = pendingInAppLinkHrefRef.current;
    pendingSelectPolicyRef.current = null;
    pendingInAppLinkHrefRef.current = null;
    if (href) {
      handleDiscardChanges();
      navigate(href, { replace: true });
    } else if (pending) {
      handleDiscardChanges();
      navigate(
        `/user-profile?policy=${encodeURIComponent(pending.policyNumber)}`,
        { replace: true },
      );
    } else {
      handleDiscardChanges();
    }
  };

  if (loading || !selectedPolicy) {
    return (
      <div className="min-w-0 lg:col-span-2">
        <Card className="profile-contact-card" style={{ overflow: 'visible' }}>
          <div className="flex justify-center py-16">
            <Spinner id="policy-contact-spinner" size={60} />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-w-0 lg:col-span-2">
      <div ref={policyContactCardRef}>
        <Card className="profile-contact-card" style={{ overflow: 'visible' }}>
          <div className="mb-[44px] flex flex-row items-center justify-between">
            <Typography
              id="policy-contact-heading"
              component="h2"
              type="h2"
              className="m-0 !tracking-[-0.31px] !text-legacy-textDarkSecondary"
            >
              Policy Contact Information
            </Typography>
            {!isEditMode && !disableEndorsements && (
              <Button
                type="ghost"
                id="profile-edit-button"
                data-dd-action-name="profile.policy_contact.edit"
                onClick={() => {
                  syncFormFromPolicyContact();
                  setBlurred({
                    email: false,
                    phone: false,
                    isPaperless: false,
                  });
                  setIsEditMode(true);
                  setShowProfileSaveErrorBanner(false);
                  setShowProfileSaveSuccessBanner(false);
                }}
              >
                <span>
                  <Icon
                    name="MdEdit"
                    fontSize={16}
                    color={LegacyColors.primary}
                    ariaHidden
                  />
                </span>
                Edit
              </Button>
            )}
            {isEditMode && (
              <div className="flex gap-2">
                <Button
                  type="ghost"
                  htmlType="button"
                  id="profile-close-edit"
                  data-dd-action-name="profile.policy_contact.close"
                  onClick={handleCloseEdit}
                >
                  Close
                </Button>
                <Button
                  type="primary"
                  id="profile-save-edit"
                  data-dd-action-name="profile.policy_contact.save"
                  onClick={handleProfileSave}
                  disabled={saveDisable}
                  className="profile-toolbar-save"
                  style={{
                    backgroundColor: saveDisable
                      ? `${LegacyColors.primary}80`
                      : LegacyColors.primary,
                    cursor: saveDisable ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSubmitting ? 'Saving…' : 'Save'}
                </Button>
              </div>
            )}
          </div>

          {showProfileSaveErrorBanner && (
            <div
              className="mb-[24px] w-full max-w-full box-border rounded-[8px] border border-solid border-legacy-error bg-legacy-errorBackground p-[13px] text-left"
              id="profile-save-error-banner"
              role="alert"
            >
              <Typography
                type="error"
                component="p"
                className="!mb-0 !text-left"
              >
                Uh-Oh! We&apos;re unable to process your request at this time.
                Please contact our customer service team at (800) 748-2030.
              </Typography>
            </div>
          )}

          {showProfileSaveSuccessBanner && (
            <div
              className="mb-[24px] w-full max-w-full box-border rounded-[8px] border border-solid border-legacy-success bg-legacy-successLight p-[13px] text-left"
              id="profile-save-success-banner"
              role="status"
            >
              <Typography
                component="p"
                className="!mb-0 !text-left !text-legacy-success"
              >
                Contact information updated. Changes may take up to 24 hours to
                reflect.
              </Typography>
            </div>
          )}

          <div className="relative mb-[24px] w-full">
            <Typography
              type="caption1"
              component="label"
              htmlFor="profile-policy-trigger"
              className="mb-[8px] block"
            >
              Policy
            </Typography>
            <div className="relative w-full">
              <div
                ref={triggerRef}
                id="profile-policy-trigger"
                data-testid="profile-policy-trigger"
                data-dd-action-name="profile.policy_selector.open"
                className={`flex w-full min-w-0 items-center justify-between gap-2 rounded-[8px] border border-border-light px-[12px] py-[10px] text-legacy-primary text-[14px] ${
                  multiplePolicies ? 'cursor-pointer hover:opacity-80' : ''
                }`}
                onClick={
                  multiplePolicies
                    ? () => setOpenSelectPolicy(!openSelectPolicy)
                    : undefined
                }
                onKeyUp={
                  multiplePolicies
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setOpenSelectPolicy(!openSelectPolicy);
                        }
                      }
                    : undefined
                }
                role={multiplePolicies ? 'button' : undefined}
                tabIndex={multiplePolicies ? 0 : undefined}
              >
                <Typography
                  type="caption2"
                  component="span"
                  className="min-w-0 flex-1 text-left !text-legacy-textDark"
                >
                  {`${selectedPolicy?.property?.street}${
                    selectedPolicy?.property?.apt
                      ? `, ${selectedPolicy?.property?.apt}`
                      : ''
                  }, ${selectedPolicy?.property?.city}, ${selectedPolicy?.property?.state} ${formats.zipFormat(selectedPolicy?.property?.zip)}`}
                </Typography>
                {multiplePolicies && (
                  <Icon
                    id="select-icon"
                    ariaHidden
                    name={!openSelectPolicy ? 'chevronDown' : 'chevronUp'}
                    color={LegacyColors.textDark}
                    fontSize={10}
                    className="shrink-0"
                    style={{ verticalAlign: 'middle', marginTop: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenSelectPolicy(!openSelectPolicy);
                    }}
                  />
                )}
              </div>
              {multiplePolicies && (
                <SelectPolicy
                  id="profile-select-policy"
                  policies={policies}
                  openSelectPolicy={openSelectPolicy}
                  onSelect={handlePolicySelect}
                  onClose={() => setOpenSelectPolicy(false)}
                  triggerRef={triggerRef}
                />
              )}
            </div>
            {isEditMode && (
              <Typography
                type="caption3"
                component="p"
                className="mb-0 mt-[8px] block"
              >
                Any changes made will reflect on your policy.
              </Typography>
            )}
          </div>

          <div className="grid gap-[16px] mb-[16px]">
            {showCompanyName ? (
              <div className="min-w-0">
                <PolicyRow
                  label="Company/DBA Name"
                  value={formatName(organizationName)}
                  vertical
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
                <div className="min-w-0">
                  <PolicyRow
                    label="First Name"
                    value={formatName(firstName)}
                    vertical
                    labelType="caption7"
                    labelClassName="mb-[4px] block"
                    labelStyle={{ display: 'block' }}
                    valueClassName="!text-legacy-textDark"
                  />
                </div>
                <div className="min-w-0">
                  <PolicyRow
                    label="Last Name"
                    value={formatName(lastName)}
                    vertical
                    labelType="caption7"
                    labelClassName="mb-[4px] block"
                    labelStyle={{ display: 'block' }}
                    valueClassName="!text-legacy-textDark"
                  />
                </div>
              </div>
            )}

            <div className="min-w-0">
              <div className="mb-[4px] flex flex-wrap items-center gap-1">
                <Typography type="caption1" component="div" className="block">
                  Policy Contact Email
                </Typography>
                <Tooltip
                  ariaLabel="Policy contact email information"
                  text={
                    <span className="profile-contact-tooltip-text">
                      All policy correspondence will be sent to this email
                      address.
                    </span>
                  }
                  dataFor="profile-contact-email-title-tooltip"
                  type="info"
                  iconSize={16}
                  iconColor={LegacyColors.primary}
                  display="inline-flex"
                  className="profile-contact-email-tooltip"
                />
              </div>
              {isEditMode ? (
                <div>
                  <div className="flex min-w-0 flex-row flex-nowrap items-center">
                    <div className="mr-[8px] flex h-[44px] w-[18px] shrink-0 items-center justify-center">
                      <Icon
                        name="MdMailOutline"
                        fontSize={18}
                        color={LegacyColors.switch}
                        ariaHidden
                      />
                    </div>
                    <FormField
                      error={
                        blurred.email &&
                        (emailStatus === 'invalid' || errors.email)
                          ? 'Please provide a valid email address.'
                          : ''
                      }
                      icon={
                        <FormFieldIcon>
                          <Tooltip
                            ariaLabel="Email Tooltip"
                            iconSize={14}
                            text="NOTE: This email is used for account access only. Policy correspondence will be directed to the email address found on your policy declaration page."
                            dataFor="create-account-email-tooltip"
                          />
                        </FormFieldIcon>
                      }
                    >
                      <Input
                        name="email"
                        type="email"
                        id="profile-contact-email"
                        onChange={(e) => {
                          const nextValue = e.target.value.replace(/^\s+/, '');
                          e.target.value = nextValue;
                          handleChange(e);
                        }}
                        onBlur={(e) => markBlurred('email', e)}
                        value={values.email}
                        error={emailStatus === 'invalid' || errors.email}
                        required
                        aria-required
                      />
                    </FormField>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-[8px]">
                  <Icon
                    name="MdMailOutline"
                    fontSize={18}
                    color={LegacyColors.switch}
                    ariaHidden
                  />
                  <Typography type="caption2" component="span">
                    {contactEmail || '—'}
                  </Typography>
                </div>
              )}
            </div>

            <div className="min-w-0">
              <Typography type="caption1" component="div" className="mb-[4px]">
                Phone Number
              </Typography>
              {isEditMode ? (
                <div>
                  <div className="flex min-w-0 flex-row flex-nowrap items-center">
                    <div className="mr-[8px] flex h-[44px] w-[18px] shrink-0 items-center justify-center">
                      <Icon
                        name="HiOutlinePhone"
                        fontSize={18}
                        color={LegacyColors.switch}
                        ariaHidden
                      />
                    </div>
                    <FormField
                      error={
                        blurred.phone && errors.phone
                          ? 'Please provide a valid phone number.'
                          : ''
                      }
                    >
                      <MaskInput
                        name="phone"
                        type="phone"
                        id="profile-contact-phone"
                        onChange={(e) => handleChange(e)}
                        onBlur={(e) => markBlurred('phone', e)}
                        value={formats.phoneFormat(values.phone)}
                        error={blurred.phone && errors.phone}
                        success={blurred.phone && errors.phone === ''}
                      />
                    </FormField>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-[8px]">
                  <Icon
                    name="HiOutlinePhone"
                    fontSize={18}
                    color={LegacyColors.switch}
                    ariaHidden
                  />
                  <Typography type="caption2" component="span">
                    {contactPhone || '—'}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          {showPaperlessSection && (
            <>
              <hr className="border-t border-gray-200 my-[16px]" />
              <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <Typography type="h3" component="span">
                      {paperless.title}
                    </Typography>
                    <Tooltip
                      ariaLabel={
                        paperless.title === 'Go Paperless'
                          ? 'Go paperless information'
                          : 'Paperless discount information'
                      }
                      text={
                        <div className="profile-contact-tooltip-text">
                          <p>
                            By enrolling in electronic delivery of policy and
                            billing documents, you affirm you have read the
                            acknowledgment of consent, confirming your ability
                            to download and view documents via the email that is
                            listed on your policy documents.
                          </p>
                          <p>
                            <Link
                              id="Acknoledgement"
                              data-dd-action-name="profile.paperless.acknowledgment.open"
                              type="primary"
                              className="hover:underline"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowConsentAckModal(true);
                              }}
                            >
                              Click here to view full acknowledgment of consent.
                            </Link>
                          </p>
                        </div>
                      }
                      dataFor="profile-paperless-tooltip"
                      type="info"
                      iconSize={16}
                      iconColor={LegacyColors.primary}
                      display="inline-flex"
                      clickable
                      className="profile-paperless-consent-tooltip"
                    />
                  </div>
                  <Typography type="footer" component="span" className="mt-1">
                    {paperlessChange
                      ? paperless?.descriptionAfterChange
                      : paperless?.description}
                  </Typography>
                </div>
                <div className="flex shrink-0 flex-row items-center self-start sm:self-auto">
                  {!isEditMode ? (
                    <>
                      <Typography component="span" className="mr-[12px]">
                        {enableEndorsement
                          ? paperless?.statusAfterChange
                          : paperless?.status}
                      </Typography>
                      <Icon
                        name={
                          paperless.status === 'Enrolled'
                            ? 'checkCircle'
                            : 'MdClose'
                        }
                        fontSize={16}
                        color={LegacyColors.secondary}
                        ariaHidden
                        className="shrink-0"
                      />
                    </>
                  ) : paperlessChange ? (
                    <Typography component="span">
                      {paperless?.statusAfterChange}
                    </Typography>
                  ) : (
                    <Button
                      type={paperless.buttonType}
                      htmlType="button"
                      id="profile-paperless"
                      data-dd-action-name="profile.paperless"
                      onClick={() => setShowConfirmModal(true)}
                      className="profile-paperless-enroll" //
                    >
                      {paperless.buttonText}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
      <Dialog
        id="preferred-email-popup"
        type={DIALOG_TYPES.CONTENT}
        show={showConsentAckModal}
        onClose={() => setShowConsentAckModal(false)}
        ariaLabel="Consent of Electronic Delivery"
        wrapperStyle={{ maxWidth: 690, width: 690 }}
      >
        <Typography
          type="h2"
          component="h2"
          className="!text-legacy-primary text-center mt-[18px] mb-[20px]"
        >
          Consent of Electronic Delivery
        </Typography>
        <div>
          <Typography type="h3" component="h2" className="mb-[10px]">
            Acknowledgment of Consent to Electronic Delivery
          </Typography>
          <Typography type="caption2">
            I consent to accept delivery of this insurance policy and all
            comunications regarding this policy through electronic means. My
            consent applies to all policy forms, notices, and communications
            until I reject my consent to electronic delivery. I understand that
            such electronic delivery communications may include any notice of
            termination, cancellation, nonrenewal, or premium increases. I
            certify that I have access to a device suitable for connecting to
            the internet, an up-to-date internet browser, a valid email account,
            means to digitally store electronic communications sent to me, and
            software that enables me to view files in Portable Document Format
            (PDF). I understand that I must notify my insurance carrier of a
            change to my email address in order to continue to receive my policy
            forms and communications electronically. I understand that I may
            withdraw my consent to delivery at any time, and that doing so will
            remove any discounts associated with using electronic delivery. I
            understand that withdrawing my consent to electronic delivery may
            result in a increase in my premium. I understand that withdrawing my
            consent does not affect the legal validity, effectiveness, or
            enforceability of any of any policy form or communication sent to me
            prior to my withdrawal of consent. If I withraw my consent to
            electronic delivery, all policy forms and communications will be
            delivered to me in paper form by mail. I understand that I have
            right to obtain a copy of any policy form or communication made
            available and sent to me in paper form. I may request a paper copy
            of a form or communication, or withdraw my consent to electronic
            delivery, by contacting my agent or customer service representative
            by phone, email, or written communication.
          </Typography>
        </div>
      </Dialog>

      <Dialog
        id="confirm-paperless-modal"
        type={DIALOG_TYPES.CONFIRM}
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={paperless.modalTitle}
        iconName={
          paperless.subtitle !== 'Go Paperless'
            ? 'HiOutlineExclamation'
            : 'HiOutlineTag'
        }
        iconSrc={paperless.leafIcon ? goPaperlessIcon : undefined}
        iconColor={LegacyColors.primary}
        subheading={paperless.modalSubtitle}
        boxDescription={paperless.modalText}
        footerText={paperless.modalQuestion}
        primaryButtonText={paperless.buttonModalText}
        secondaryButtonText="Close"
        onPrimaryClick={() => {
          setPaperlessChange(true);
          setFormValues({ ...values, isPaperless: !isPaperless });
          setShowConfirmModal(false);
        }}
        onSecondaryClick={() => setShowConfirmModal(false)}
        ariaLabel={paperless.modalTitle}
      />

      <Dialog
        id="unsaved-policy-changes"
        type={DIALOG_TYPES.CONTENT}
        show={showUnsavedPolicyModal}
        onClose={() => setShowUnsavedPolicyModal(false)}
        title="Unsaved Policy Changes"
        primaryButtonText={isSubmitting ? 'Saving…' : 'Save and Close'}
        secondaryButtonText="Discard Changes"
        onPrimaryClick={handleSaveAndClose}
        onSecondaryClick={handleUnsavedPolicyDiscard}
        disabledPrimaryBtn={saveDisable}
        ariaLabel="Unsaved policy changes"
      >
        <Typography type="caption5" component="p" className="mb-[22px]">
          You have unsaved changes. Are you sure you want to leave? Your changes
          will be lost.
        </Typography>
      </Dialog>
    </div>
  );
};

PolicyContactCard.propTypes = {
  api: PropTypes.object.isRequired,
  policies: PropTypes.array.isRequired,
  selectedPolicy: PropTypes.object,
  loading: PropTypes.bool,
  fetchPolicy: PropTypes.func.isRequired,
};

export default PolicyContactCard;
